import { useViewMediaModal } from "@next-platform/client-common-core";
import { memo } from "react";

import useIsLoggedOutReader from "@/hooks/useIsLoggedOutReader";

import MediaFileDetailsModalContent from "./MediaFileDetailsModalContent";
import useMediaFileModalAddress from "./useMediaFileModalAddress";

export const MEDIA_FILE_DETAILS_MODAL_TEST_ID = "MediaFileDetailsModal";

type MediaFileDetailsModalProps = {
  readonly "data-testid"?: string;
};

/**
 * The details modal, mounted once so any surface can open it.
 *
 * This is only the gate. It stays mounted for the life of the app, so it reads the open file and keeps the
 * address in step, and nothing else: the reader's settings, the brand's capabilities, the translations and
 * the details pane itself all belong to `MediaFileDetailsModalContent`, which exists only while a file is on
 * screen. Holding that work open behind every page is what made opening and closing cost more each time.
 *
 * The content renders the same `MediaFileDetails` as the standalone page and adds only the chrome around it:
 * the stepping controls, the close affordance and the file id in the address. It never learns which page
 * opened it — the surrounding list arrives through `useViewMediaModal`.
 */
const MediaFileDetailsModal = ({ "data-testid": testId = MEDIA_FILE_DETAILS_MODAL_TEST_ID }: MediaFileDetailsModalProps) => {
  const { isOpen, fileId, open, close } = useViewMediaModal();
  useMediaFileModalAddress({ isOpen, fileId, open, close });
  const isLoggedOutReader = useIsLoggedOutReader();

  if (!isOpen || fileId === null || isLoggedOutReader) {
    return null;
  }

  return <MediaFileDetailsModalContent fileId={fileId} testId={testId} />;
};

export default memo(MediaFileDetailsModal);
